import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Zap, Users } from 'lucide-react';
import axios from 'axios';
import MSFLogo from '../components/MSFLogo';
import PageBackground from '../components/PageBackground';
import { normalizeImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to convert Tailwind font class to actual font family
const getFontFamily = (fontClass) => {
  const fontMap = {
    'font-heading': '"Racing Sans One", sans-serif',
    'font-sans': '"Montserrat", sans-serif',
    'font-serif': 'Georgia, serif',
    'font-mono': '"Aero Matics", sans-serif',
  };
  return fontMap[fontClass] || fontMap['font-heading'];
};

// Helper to convert Tailwind size class to actual size
const getFontSize = (sizeClass) => {
  const sizeMap = {
    'text-xs': '0.75rem',
    'text-sm': '0.875rem',
    'text-base': '1rem',
    'text-lg': '1.125rem',
    'text-xl': '1.25rem',
    'text-2xl': '1.5rem',
    'text-3xl': '1.875rem',
    'text-4xl': '2.25rem',
    'text-5xl': '3rem',
    'text-6xl': '3.75rem',
  };
  return sizeMap[sizeClass] || null;
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    landing_page_title: "MSF RACING",
    landing_page_subtitle: "FANTASY LEAGUE",
    landing_page_description: "Build your dream team. Compete for glory. Dominate the leaderboard.",
    landing_page_image: ""
  });
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/system/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <PageBackground pageKey="landing">
    <div className="min-h-screen bg-background">
      <div
        className="relative h-screen bg-cover bg-center"
        style={{
          backgroundImage: `url(${normalizeImageUrl(settings.landing_page_image)})`,
        }}
      >
        <div 
          className="absolute inset-0" 
          style={{ backgroundColor: `rgba(0,0,0,${(settings.landing_overlay_opacity || 70) / 100})` }}
        ></div>
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-4">
          {/* Main Title - Logo or Text */}
          {settings.landing_page_logo ? (
            <img 
              src={normalizeImageUrl(settings.landing_page_logo)} 
              alt="MSF Racing" 
              className="h-32 md:h-48 object-contain mb-6 animate-fade-in"
            />
          ) : (
            <h1 
              className="font-heading font-black text-6xl md:text-8xl mb-6 animate-fade-in"
              style={{
                fontFamily: getFontFamily(settings.landing_title_font),
                fontSize: getFontSize(settings.landing_title_size) || undefined,
                color: settings.landing_title_color || undefined
              }}
            >
              <span className="text-primary" style={{ color: settings.landing_title_color || undefined }}>
                {settings.landing_page_title?.split(' ')[0] || 'MSF'}
              </span> {settings.landing_page_title?.split(' ').slice(1).join(' ') || 'RACING'}
            </h1>
          )}
          
          {/* Subtitle - Logo or Text */}
          {settings.landing_page_subtitle_logo ? (
            <img 
              src={settings.landing_page_subtitle_logo} 
              alt="Fantasy League" 
              className="h-16 md:h-24 object-contain mb-6 animate-fade-in"
            />
          ) : (
            <h2 
              className="font-heading font-bold text-3xl md:text-5xl mb-6 animate-fade-in"
              style={{
                fontFamily: getFontFamily(settings.landing_subtitle_font),
                fontSize: getFontSize(settings.landing_subtitle_size) || undefined,
                color: settings.landing_subtitle_color || undefined
              }}
            >
              {settings.landing_page_subtitle || 'FANTASY LEAGUE'}
            </h2>
          )}
          
          <p 
            className="text-xl md:text-2xl text-zinc-300 max-w-3xl mb-12 animate-fade-in"
            style={{
              fontSize: getFontSize(settings.landing_desc_size) || undefined,
              color: settings.landing_desc_color || undefined
            }}
          >
            {settings.landing_page_description || 'Build your dream team. Compete for glory. Dominate the leaderboard.'}
          </p>
          <button
            onClick={handleGetStarted}
            data-testid="landing-get-started-button"
            className="btn-skew text-white px-12 py-4 font-heading font-bold text-xl uppercase tracking-wider neon-glow-hover"
            style={{
              backgroundColor: settings.landing_button_color || '#ff4400'
            }}
          >
            <span>{settings.landing_button_text || 'Get Started'}</span>
          </button>
        </div>
      </div>

      <div className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading font-black text-4xl md:text-5xl text-center mb-16">
            HOW IT <span className="text-primary">WORKS</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-card border border-zinc-800 p-8 hover:border-primary transition-colors">
              <Trophy className="text-primary mb-4" size={48} />
              <h3 className="font-heading font-bold text-2xl mb-4">Select Your Team</h3>
              <p className="text-zinc-400">
                Choose your lineup from <MSFLogo suffix="Racing" size="xs" /> (drivers & cars) or <MSFLogo suffix="Moto" size="xs" /> (riders & bikes) to build your ultimate fantasy team.
              </p>
            </div>
            <div className="bg-card border border-zinc-800 p-8 hover:border-primary transition-colors">
              <Zap className="text-primary mb-4" size={48} />
              <h3 className="font-heading font-bold text-2xl mb-4">Earn Points</h3>
              <p className="text-zinc-400">
                Score points based on real race results. Top finishers earn you maximum points!
              </p>
            </div>
            <div className="bg-card border border-zinc-800 p-8 hover:border-primary transition-colors">
              <Users className="text-primary mb-4" size={48} />
              <h3 className="font-heading font-bold text-2xl mb-4">Climb the Rankings</h3>
              <p className="text-zinc-400">
                Compete against other fans and climb to the top of the leaderboard in both series.
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-card border-t border-zinc-800 py-8 text-center text-zinc-500">
        <p>{settings.landing_footer_text || <>&copy; 2025 <MSFLogo size="xs" /> Fantasy League. All rights reserved.</>}</p>
        <div className="mt-4">
          <button
            onClick={() => navigate('/admin-login')}
            className="text-zinc-600 hover:text-primary transition-colors text-sm"
          >
            Admin Access
          </button>
        </div>
      </footer>
    </div>
    </PageBackground>
  );
}
